import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button,
    Avatar
} from '@mui/material';

import {
    AttachMoney,
    CleaningServices,
    PeopleAlt,
    EmojiObjects,
    ErrorOutline,
    CheckCircleOutline,
    Close,
    Restaurant,
    Bed,
    LocalLaundryService,
    VolumeUp,
    LocalAtm,
    Lock,
    WbSunny,
    Nightlight,
    Group,
    Gavel,
    TipsAndUpdates
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    {
        id: 'introduction',
        label: 'ルームシェアが人気！でもトラブルも多発？',
    },
    {
        id: 'money-trouble',
        label: '金銭トラブル事例30連発',
    },
    {
        id: 'lifestyle-trouble',
        label: '生活習慣トラブル事例35連発',
    },
    {
        id: 'human-trouble',
        label: '人間関係トラブル事例35連発',
    },
    {
        id: 'solution',
        label: 'トラブルを防ぐ・解決するためのヒント',
    },
    {
        id: 'summary',
        label: 'まとめ',
    },
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 3, pb: 8 }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)} sx={{ minWidth: 36 }}>
                                閉じる
                            </Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{
                                        minWidth: 32,
                                        minHeight: 32,
                                        bgcolor: '#f5f5f5',
                                        color: '#555',
                                        fontWeight: 'bold',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 1,
                                        fontSize: '1.15rem'
                                    }}>
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.95rem', textAlign: 'left', p: 0, m: 0 }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアが人気！でもトラブルも多発？
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    近年、住居費の節約や新しい人間関係を求めて、ルームシェアを選ぶ人が急増しています。オシャレなシェアハウスや、友人同士での同居――始めはワクワクしても、実際に生活を始めると「こんなはずじゃなかった！」と感じる人も多いのが現実です。
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアで起こるトラブルは、金銭問題や生活習慣のズレ、さらには人間関係のもつれまで多岐にわたります。本記事では、リアルな「ルームシェアのトラブル事例」100連発を、ジャンルごとに一挙紹介！さらに、専門家の視点で解決策や未然に防ぐヒントも分かりやすくまとめました。
                </Typography>
                <Box sx={{ my: 3 }}>
                    
                </Box>
                <Typography variant="body2" color="text.secondary">
                    これからルームシェアを始める方も、すでに悩んでいる方も、リアルな「失敗例」から学び、安心・快適なシェア生活を目指しましょう。
                </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 金銭トラブル */}
            <Box id="money-trouble" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <AttachMoney sx={{ verticalAlign: 'middle', mr: 1, color: '#388e3c' }} />
                    金銭トラブル事例30連発
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアのトラブルで最も多いのが「お金」に関するもの。家賃や光熱費だけでなく、日用品の購入、食費の分担、敷金・礼金など、さまざまな場面で問題が起こります。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><LocalAtm color="error" /></ListItemIcon>
                                    <ListItemText primary="家賃の振込を1人がまとめて担当→支払い遅延が発覚" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><LocalAtm color="error" /></ListItemIcon>
                                    <ListItemText primary="光熱費の精算を曖昧にし、損得感情で険悪に" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><LocalAtm color="error" /></ListItemIcon>
                                    <ListItemText primary="共用日用品の購入費を払わない人が出現" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><LocalAtm color="error" /></ListItemIcon>
                                    <ListItemText primary="「家賃は払った」と主張するが実際は未納" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><LocalAtm color="warning" /></ListItemIcon>
                                    <ListItemText primary="退去時の敷金トラブル（誰がいくら負担？）" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><LocalAtm sx={{ color: '#ff7043' }} /></ListItemIcon>
                                    <ListItemText primary="家賃の負担割合が不公平（部屋の広さ・設備差）" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><AttachMoney color="secondary" /></ListItemIcon>
                                    <ListItemText primary="友人同士だからと口約束でお金のやり取り" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Lock color="disabled" /></ListItemIcon>
                                    <ListItemText primary="共用金庫の管理がずさんで紛失や盗難疑惑" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><AttachMoney color="error" /></ListItemIcon>
                                    <ListItemText primary="家賃を滞納したまま夜逃げ" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><AttachMoney color="secondary" /></ListItemIcon>
                                    <ListItemText primary="共用家電の購入費用で揉める" />
                                </ListItem>
                                {/* さらに20件 */}
                                <ListItem>
                                    <ListItemIcon><AttachMoney color="error" /></ListItemIcon>
                                    <ListItemText primary="家賃や光熱費の負担割合について合意が取れていなかった" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><LocalAtm sx={{ color: '#ffa000' }} /></ListItemIcon>
                                    <ListItemText primary="公共料金の名義が1人だけでリスクが集中" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><LocalAtm sx={{ color: '#ff7043' }} /></ListItemIcon>
                                    <ListItemText primary="「今月は厳しい」と支払いを先延ばし" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><AttachMoney color="error" /></ListItemIcon>
                                    <ListItemText primary="日用品の立て替えに不満が募る" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><AttachMoney color="secondary" /></ListItemIcon>
                                    <ListItemText primary="共用の調味料・食材を無断で使う" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><AttachMoney color="error" /></ListItemIcon>
                                    <ListItemText primary="修繕費やトラブル時の補償負担に納得できない" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Lock color="disabled" /></ListItemIcon>
                                    <ListItemText primary="家計管理アプリの共有パスワードを勝手に変更" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><LocalAtm color="secondary" /></ListItemIcon>
                                    <ListItemText primary="共用費の予算超過で言い争い" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><AttachMoney color="disabled" /></ListItemIcon>
                                    <ListItemText primary="「出ていくから家賃はもう払わない」と一方的主張" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><LocalAtm color="error" /></ListItemIcon>
                                    <ListItemText primary="キャッシュレス決済でトラブル（送金ミス・未確認）" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><AttachMoney color="secondary" /></ListItemIcon>
                                    <ListItemText primary="「細かいお金は気にしない」と言う割に毎回損している人がいる" />
                                </ListItem>
                                {/* さらに10件（省略） */}
                                <ListItem>
                                    <ListItemIcon><LocalAtm sx={{ color: '#ff7043'}}/></ListItemIcon>
                                    <ListItemText primary="誰か1人が代理で全て支払うことで負担やストレス" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><AttachMoney color="error" /></ListItemIcon>
                                    <ListItemText primary="外部の友人を連れてきて、食費や光熱費の精算をせずにトラブル" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><AttachMoney color="secondary" /></ListItemIcon>
                                    <ListItemText primary="ペット関連費用の分担でモメる" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><LocalAtm color="disabled" /></ListItemIcon>
                                    <ListItemText primary="家賃が値上がりした際の負担割合でもめる" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><AttachMoney sx={{ color: '#ffa000' }} /></ListItemIcon>
                                    <ListItemText primary="家具・家電の所有権や処分費用で言い争い" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><AttachMoney color="error" /></ListItemIcon>
                                    <ListItemText primary="「自分はあまり使っていない」と費用負担を渋る" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Lock color="disabled" /></ListItemIcon>
                                    <ListItemText primary="共用費の管理係をめぐって責任の押し付け合い" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><AttachMoney color="secondary" /></ListItemIcon>
                                    <ListItemText primary="飲み会・パーティー費用の割り勘で険悪" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><AttachMoney color="error" /></ListItemIcon>
                                    <ListItemText primary="「たった数百円」で大喧嘩に発展" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: '#f7fafc'}}>
                            <Typography variant="subtitle1" gutterBottom>
                                <EmojiObjects sx={{ verticalAlign: 'middle', mr: 1, color: '#0288d1' }} />
                                金銭トラブルを防ぐためのチェックポイント
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="家賃・光熱費など支払いルールを明文化し共有" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="家計管理アプリやシートで精算記録を残す" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="支払い担当者を定期的にローテーション" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="共用費・個人費の区別を明確に" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="口約束ではなく必ずメモ・LINEグループ等で記録" />
                                </ListItem>
                            </List>
                        </Paper>
                        <Box sx={{ my: 2 }}>
                            
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 生活習慣トラブル */}
            <Box id="lifestyle-trouble" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <CleaningServices sx={{ verticalAlign: 'middle', mr: 1, color: '#1976d2' }} />
                    生活習慣トラブル事例35連発
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    共同生活では「当たり前」が人それぞれ違うもの。掃除やゴミ出し、食事、生活リズムなどの些細なズレが、時に大きなストレスや衝突の火種となります。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><CleaningServices color="error" /></ListItemIcon>
                                    <ListItemText primary="ゴミ出しを誰もしない、または押し付け合い" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Restaurant color="warning" /></ListItemIcon>
                                    <ListItemText primary="キッチンを使った後に汚れっぱなし" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Bed color="secondary" /></ListItemIcon>
                                    <ListItemText primary="夜型・朝型の生活リズムが全員バラバラ" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><VolumeUp color="error" /></ListItemIcon>
                                    <ListItemText primary="夜中の大音量での音楽・動画視聴" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><LocalLaundryService color="primary" /></ListItemIcon>
                                    <ListItemText primary="洗濯機の使い方・順番で揉める" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><WbSunny color="warning" /></ListItemIcon>
                                    <ListItemText primary="カーテン・照明の使い方や明るさの好みが合わない" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Nightlight color="primary" /></ListItemIcon>
                                    <ListItemText primary="深夜の帰宅・外出で他人を起こしてしまう" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CleaningServices color="secondary" /></ListItemIcon>
                                    <ListItemText primary="共用スペースの掃除頻度をめぐる争い" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Restaurant color="error" /></ListItemIcon>
                                    <ListItemText primary="冷蔵庫の中の食材を無断で食べられる" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Bed color="warning" /></ListItemIcon>
                                    <ListItemText primary="来客・友人の宿泊を無断で許可" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CleaningServices color="error" /></ListItemIcon>
                                    <ListItemText primary="お風呂・トイレの使い方が汚い" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><LocalLaundryService color="primary" /></ListItemIcon>
                                    <ListItemText primary="洗濯物の放置で臭い・場所の取り合い" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CleaningServices color="warning" /></ListItemIcon>
                                    <ListItemText primary="共用掃除道具を勝手に使い、元に戻さない" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><VolumeUp color="secondary" /></ListItemIcon>
                                    <ListItemText primary="楽器演奏やテレビ音量で揉める" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Restaurant color="primary" /></ListItemIcon>
                                    <ListItemText primary="食事の匂い（ニンニク・スパイス等）が気になる" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Bed color="error" /></ListItemIcon>
                                    <ListItemText primary="寝室のドアを開けっ放しでプライバシー無視" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><WbSunny color="secondary" /></ListItemIcon>
                                    <ListItemText primary="エアコン設定温度の好みが合わず冷暖房戦争" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Nightlight color="warning" /></ListItemIcon>
                                    <ListItemText primary="夜間の照明を消さずに寝る人がいる" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CleaningServices color="secondary" /></ListItemIcon>
                                    <ListItemText primary="消耗品を使い切っても補充しない" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><VolumeUp color="error" /></ListItemIcon>
                                    <ListItemText primary="電話・ビデオ通話の声が大きい" />
                                </ListItem>
                                {/* さらに15件（省略） */}
                                <ListItem>
                                    <ListItemIcon><Restaurant color="warning" /></ListItemIcon>
                                    <ListItemText primary="食器をすぐ洗わず山積みに" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CleaningServices color="secondary" /></ListItemIcon>
                                    <ListItemText primary="共用のタオルやバスマットを勝手に使う" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Bed color="primary" /></ListItemIcon>
                                    <ListItemText primary="寝室の換気・加湿で意見が合わない" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><LocalLaundryService color="error" /></ListItemIcon>
                                    <ListItemText primary="洗濯槽やフィルターの掃除をしない" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Nightlight color="primary" /></ListItemIcon>
                                    <ListItemText primary="就寝・起床時間が極端にズレている" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CleaningServices color="error" /></ListItemIcon>
                                    <ListItemText primary="ペットの世話・掃除がなされない" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Bed color="secondary" /></ListItemIcon>
                                    <ListItemText primary="寝具や部屋の消臭・清潔感でトラブル" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><WbSunny color="primary" /></ListItemIcon>
                                    <ListItemText primary="休日の朝早くから騒がしい" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Restaurant color="error" /></ListItemIcon>
                                    <ListItemText primary="冷蔵庫のスペース取り合い" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CleaningServices color="secondary" /></ListItemIcon>
                                    <ListItemText primary="共用スペースの私物放置" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><VolumeUp color="secondary" /></ListItemIcon>
                                    <ListItemText primary="目覚まし時計の音が大きすぎる" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><LocalLaundryService color="primary" /></ListItemIcon>
                                    <ListItemText primary="洗濯機の使い終わりを放置" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Bed color="error" /></ListItemIcon>
                                    <ListItemText primary="寝室での飲食禁止ルールを守らない" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CleaningServices color="warning" /></ListItemIcon>
                                    <ListItemText primary="掃除スケジュールが守られない" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Restaurant color="primary" /></ListItemIcon>
                                    <ListItemText primary="共用調味料の消費が激しい" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Nightlight color="primary" /></ListItemIcon>
                                    <ListItemText primary="夜更かし・早起きで騒音トラブル" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: '#f7fafc' }}>
                            <Typography variant="subtitle1" gutterBottom>
                                <EmojiObjects sx={{ verticalAlign: 'middle', mr: 1, color: '#0288d1' }} />
                                生活習慣トラブルを防ぐためのポイント
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="掃除・ゴミ出しなど家事分担表を作る" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="生活リズム・音量など相手の事情を尊重" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="共用スペースの使用ルールを事前に決める" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="私物・消耗品の管理は明確に" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="定期的な「ルームミーティング」を設ける" />
                                </ListItem>
                            </List>
                        </Paper>
                        <Box sx={{ my: 2 }}>
                            <img
                                src="/images/blog/cleaning-group_roomshare-trouble-100.jpg"
                                alt="休日の午前中、明るいリビングで複数のルームメイトが協力して掃除や片付けをしている様子。床に掃除機をかける人、テーブルを拭く人など、家事分担が自然に行われている雰囲気"
                                style={{ width: '100%', borderRadius: 12 }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 人間関係トラブル */}
            <Box id="human-trouble" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <PeopleAlt sx={{ verticalAlign: 'middle', mr: 1, color: '#7b1fa2' }} />
                    人間関係トラブル事例35連発
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    価値観やコミュニケーションの違いからくる「人間関係のトラブル」は、時に金銭や生活習慣の問題より根深いものになりがちです。些細な誤解や、相性・距離感のズレがシェア生活を難しくします。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><Group color="error" /></ListItemIcon>
                                    <ListItemText primary="グループ内で派閥ができて孤立する人が出る" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Group color="warning" /></ListItemIcon>
                                    <ListItemText primary="言いにくいことを避けて問題が放置される" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><PeopleAlt color="secondary" /></ListItemIcon>
                                    <ListItemText primary="無視・陰口など陰湿な空気が漂う" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Group color="error" /></ListItemIcon>
                                    <ListItemText primary="「あの人が嫌い」と直接対立" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><PeopleAlt color="primary" /></ListItemIcon>
                                    <ListItemText primary="恋愛関係がこじれて同居に支障" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Group color="secondary" /></ListItemIcon>
                                    <ListItemText primary="価値観の違いで小さな嫌がらせ" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><PeopleAlt color="warning" /></ListItemIcon>
                                    <ListItemText primary="プライベートの詮索が過剰" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Group color="error" /></ListItemIcon>
                                    <ListItemText primary="仕事・勉強の邪魔をされる" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><PeopleAlt color="error" /></ListItemIcon>
                                    <ListItemText primary="ちょっとした注意が大喧嘩に発展" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Group color="warning" /></ListItemIcon>
                                    <ListItemText primary="LINEグループなどSNSでのトラブル" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><PeopleAlt color="secondary" /></ListItemIcon>
                                    <ListItemText primary="友人・恋人を頻繁に連れてきて他人が居づらい" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Group color="primary" /></ListItemIcon>
                                    <ListItemText primary="イベント・パーティーの頻度で揉める" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><PeopleAlt color="error" /></ListItemIcon>
                                    <ListItemText primary="「人見知り」と「社交的」で温度差" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Group color="secondary" /></ListItemIcon>
                                    <ListItemText primary="ちょっとした嘘や隠し事が不信感に" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><PeopleAlt color="primary" /></ListItemIcon>
                                    <ListItemText primary="ちょっとした借り・貸しで気まずさ" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Group color="warning" /></ListItemIcon>
                                    <ListItemText primary="年齢差・国籍差で価値観ギャップ" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><PeopleAlt color="secondary" /></ListItemIcon>
                                    <ListItemText primary="「片付けて」と言えずストレスを溜める" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Group color="primary" /></ListItemIcon>
                                    <ListItemText primary="誰か1人に頼りがちで負担が集中" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><PeopleAlt color="error" /></ListItemIcon>
                                    <ListItemText primary="仕事・プライベートの境界が曖昧" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Group color="secondary" /></ListItemIcon>
                                    <ListItemText primary="ルール違反の指摘で逆ギレされる" />
                                </ListItem>
                                {/* さらに15件（省略） */}
                                <ListItem>
                                    <ListItemIcon><PeopleAlt color="secondary" /></ListItemIcon>
                                    <ListItemText primary="「自分は悪くない」と謝らない" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Group color="error" /></ListItemIcon>
                                    <ListItemText primary="退去時に関係がこじれる" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><PeopleAlt color="warning" /></ListItemIcon>
                                    <ListItemText primary="他人のSNS投稿でプライバシー侵害" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Group color="secondary" /></ListItemIcon>
                                    <ListItemText primary="相談できずに問題が悪化" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><PeopleAlt color="primary" /></ListItemIcon>
                                    <ListItemText primary="気遣いが裏目に出てギクシャク" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Group color="warning" /></ListItemIcon>
                                    <ListItemText primary="他人の物を勝手に使う（本・家電等）" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><PeopleAlt color="error" /></ListItemIcon>
                                    <ListItemText primary="「挨拶しない」と評価を下げ合う" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Group color="primary" /></ListItemIcon>
                                    <ListItemText primary="「空気読んで」と押し付け合い" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><PeopleAlt color="secondary" /></ListItemIcon>
                                    <ListItemText primary="家庭環境・宗教観の違いに戸惑う" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Group color="error" /></ListItemIcon>
                                    <ListItemText primary="一度険悪になると修復が難しい" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><PeopleAlt color="warning" /></ListItemIcon>
                                    <ListItemText primary="自分の話ばかりする人に疲れる" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Group color="secondary" /></ListItemIcon>
                                    <ListItemText primary="仲裁役がいなくてトラブルが長期化" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><PeopleAlt color="primary" /></ListItemIcon>
                                    <ListItemText primary="「仲良し」前提で距離感に困る" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Group color="warning" /></ListItemIcon>
                                    <ListItemText primary="一方的な我慢や遠慮が爆発" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><PeopleAlt color="error" /></ListItemIcon>
                                    <ListItemText primary="「簡単に引っ越せない」と悩みを抱え込む" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: '#f7fafc' }}>
                            <Typography variant="subtitle1" gutterBottom>
                                <EmojiObjects sx={{ verticalAlign: 'middle', mr: 1, color: '#0288d1' }} />
                                人間関係トラブルを防ぐためのアドバイス
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="定期的なコミュニケーションタイムを設ける" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="問題は早めに伝え、感情的にならない" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="ルームシェア契約書・ルールブックを活用" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="適度な距離感・プライベートを尊重" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="第三者（管理会社・仲介者等）への相談も視野に" />
                                </ListItem>
                            </List>
                        </Paper>
                        <Box sx={{ my: 2 }}>
                            
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 解決策まとめ */}
            <Box id="solution" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <TipsAndUpdates sx={{ verticalAlign: 'middle', mr: 1, color: '#fbc02d' }} />
                    トラブルを防ぐ・解決するためのヒント
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    これまでの事例を踏まえ、ルームシェアで起こりがちなトラブルを未然に防ぎ、また起こってしまった場合に円満解決するためのポイントをまとめました。
                </Typography>
                <Box sx={{ mb: 3 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                    <Gavel sx={{ verticalAlign: 'middle', mr: 1, color: '#0288d1' }} />
                                    事前予防のための実践テクニック
                                </Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon><CheckCircleOutline color="primary" /></ListItemIcon>
                                        <ListItemText primary="入居前に必ずルールブックや同意書を作成" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><CheckCircleOutline color="primary" /></ListItemIcon>
                                        <ListItemText primary="家計・家事・生活リズムについて話し合い" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><CheckCircleOutline color="primary" /></ListItemIcon>
                                        <ListItemText primary="トラブル時の相談・解決フローを決めておく" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><CheckCircleOutline color="primary" /></ListItemIcon>
                                        <ListItemText primary="誰かに負担が偏らない仕組みを作る" />
                                    </ListItem>
                                </List>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: '#f9fbe7' }}>
                                <Typography variant="subtitle1" gutterBottom>
                                    <EmojiObjects sx={{ verticalAlign: 'middle', mr: 1, color: '#fbc02d' }} />
                                    トラブル発生時の対処ポイント
                                </Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon><CheckCircleOutline color="primary" /></ListItemIcon>
                                        <ListItemText primary="感情的にならず冷静に事実を確認" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><CheckCircleOutline color="primary" /></ListItemIcon>
                                        <ListItemText primary="直接言い合いが難しい場合はメッセージや手紙も活用" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><CheckCircleOutline color="primary" /></ListItemIcon>
                                        <ListItemText primary="第三者の意見（管理会社・知人等）を取り入れる" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><CheckCircleOutline color="primary" /></ListItemIcon>
                                        <ListItemText primary="どうしても解決できない場合は退去も選択肢" />
                                    </ListItem>
                                </List>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>
                <Typography variant="body2" color="text.secondary">
                    トラブルをゼロにすることは難しいですが、事前の取り決めや日々のコミュニケーションで大きな問題を未然に防ぐことができます。
                </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* まとめ */}
            <Box id="summary" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめ
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアは、金銭面のメリットや新しい出会い、刺激的な毎日をもたらす一方で、さまざまなトラブルがつきものです。大切なのは「他人と暮らす」ための丁寧な準備と、日々の小さな気遣い。<br />
                    本記事で紹介した100の事例と予防・解決策を参考に、より快適で楽しいシェア生活を実現してください。
                </Typography>
                <Box sx={{ my: 3 }}>
                    <img
                        src="/images/blog/peaceful-share_roomshare-trouble-100.jpg"
                        alt="夕暮れ時の落ち着いたリビングで、ルームメイトたちがリラックスしてソファに座り、談笑しながらお茶を楽しんでいる。温かい照明と穏やかな表情が、安心できる共同生活の雰囲気を伝えるシーン"
                        style={{ width: '100%', borderRadius: 12 }}
                    />
                </Box>
                <Typography variant="body2" color="text.secondary">
                    ルームシェアの「理想」と「現実」を知り、あなたらしい快適な住まいを手に入れましょう。
                </Typography>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-trouble-100',
    slug: 'roomshare-trouble-100',
    title: 'ルームシェアの「トラブル事例」100連発',
    excerpt: '金銭トラブル、生活習慣トラブル、人間関係トラブル、解決策まとめを徹底解説。100のリアルなルームシェア失敗例と防止策を網羅。',
    coverImage: '/images/blog/cover-roomshare-trouble_roomshare-trouble-100.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。木目調のテーブルとナチュラルなインテリアが特徴的で、快適な共同生活の雰囲気を伝えるカバー画像',
    publishedAt: '2024-06-10T09:00:00+09:00',
    isPublished: true,
    categories: ['ルームシェア', '生活トラブル', '住まい'],
    tags: ['ルームシェア', 'トラブル', '金銭', '人間関係', '生活習慣'],
    author: {
        name: '清水 大輔',
        avatar: '/images/avatars/roomshare-expert.jpg',
        bio: 'ルームシェア歴10年以上。多数のシェアハウス運営と相談実績をもとに、快適な共同生活のコツやトラブル対策を発信中。',
        profilePicture: '/images/avatars/roomshare-expert.jpg'
    },
    viewCount: 1324,
    contentComponent: PostContent
};

export default post;